import { useEffect, useState } from 'react';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import { employeeService } from '../services/service';
import type { SelectOption, UseFormAddTag, UseFormAddTagProps, UseFormAddTagValidationErrors } from '../types/hooks';
import type { Alert, EmployeeTagRange } from '../types/services';
import { useAuthorization } from '../../../../contexts/AuthorizationContext';
import { usePermisoEmpleado as usePermiso } from '../../../../auth/permisosModulos';

const emptyValidationErrors: UseFormAddTagValidationErrors = {
    tags: { error: false, message: '' },
    startDate: { error: false, message: '' },
    endDate: { error: false, message: '' },
};

const getRangeKey = (range: EmployeeTagRange) => String(range.employee_r_tag_id ?? `${range.tag_id}-${range.start_date}-${range.end_date}`);

const toDateOnly = (value: string) => value.split('T')[0];

const deduplicateRanges = (ranges: EmployeeTagRange[]) => {
    const seen = new Set<string>();

    return ranges.filter((range) => {
        const key = `${range.tag_id}-${toDateOnly(range.start_date)}-${toDateOnly(range.end_date)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

type RangeChanges = Record<string, { tagId: number; startDate: string; endDate: string }>;

const toDraftRanges = (ranges: EmployeeTagRange[], changes: RangeChanges = {}, deletedKeys: Set<string> = new Set()) =>
    ranges.flatMap((range) => {
        const key = getRangeKey(range);
        if (deletedKeys.has(key)) return [];

        const change = changes[key];
        return [{
            tagId: String(change?.tagId ?? range.tag_id),
            startDate: change?.startDate ?? toDateOnly(range.start_date),
            endDate: change?.endDate ?? toDateOnly(range.end_date),
        }];
    });

const useFormAddTag = ({ rowsSelected, option, setOption, reload }: UseFormAddTagProps): UseFormAddTag => {
    const { t } = useTranslation('workers.form.assign_tag');
    const { showAlert, closeAlert } = useRbSystemAlert();
    const { authorization, hasTagAccess } = useAuthorization();

    const [openForm, setOpenForm] = useState(false);
    const [tagCatalog, setTagCatalog] = useState<SelectOption[]>([]);
    const [tags, setTags] = useState<SelectOption[]>([]);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [searchTag, setSearchTag] = useState('');
    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [overwrite, setOverwrite] = useState(false);
    const [tab, setTab] = useState<'new' | 'existing'>('new');
    const [editingRangeKey, setEditingRangeKey] = useState<string | null>(null);
    const [existingRanges, setExistingRanges] = useState<EmployeeTagRange[]>([]);
    const [draftExistingRanges, setDraftExistingRanges] = useState<EmployeeTagRange[]>([]);
    const [draftRangeChanges, setDraftRangeChanges] = useState<RangeChanges>({});
    const [deletedRangeKeys, setDeletedRangeKeys] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [loadingTags, setLoadingTags] = useState(false);
    const [loadingRanges, setLoadingRanges] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [showMessage, setShowMessage] = useState(false);
    const [validationErrors, setValidationErrors] = useState<UseFormAddTagValidationErrors>(emptyValidationErrors);

    const canAssign = usePermiso('asignar-etiqueta');
    const canCreateTag = usePermiso('crear-etiqueta');
    const hasInvalidDateRange = Boolean(startDate && endDate && !endDate.isAfter(startDate, 'day'));
    const isEditingExisting = editingRangeKey !== null;
    const hasDraftExistingChanges = JSON.stringify(toDraftRanges(existingRanges)) !== JSON.stringify(toDraftRanges(draftExistingRanges, draftRangeChanges, deletedRangeKeys));
    const canSubmit = Boolean(
        (tab === 'new' || isEditingExisting || hasDraftExistingChanges) &&
        rowsSelected.length > 0 &&
        (hasDraftExistingChanges && !isEditingExisting ? true : isEditingExisting ? selectedTags.length === 1 : selectedTags.length > 0) &&
        (hasDraftExistingChanges && !isEditingExisting ? true : startDate?.isValid()) &&
        (hasDraftExistingChanges && !isEditingExisting ? true : endDate?.isValid()) &&
        !hasInvalidDateRange &&
        !loading,
    );

    const alert: Alert = {
        t,
        showAlert: (severity, message) => showAlert(severity, message),
        closeAlert,
    };

    const resetForm = () => {
        setTagCatalog([]);
        setTags([]);
        setSelectedTags([]);
        setSearchTag('');
        setStartDate(null);
        setEndDate(null);
        setOverwrite(false);
        setTab('new');
        setExistingRanges([]);
        setDraftExistingRanges([]);
        setDraftRangeChanges({});
        setDeletedRangeKeys(new Set());
        setEditingRangeKey(null);
        setValidationMessage('');
        setShowMessage(false);
        setValidationErrors(emptyValidationErrors);
    };

    const hasChanges = () => selectedTags.length > 0 || startDate !== null || endDate !== null || overwrite || hasDraftExistingChanges;

    const closeForm = () => {
        setOpenForm(false);
        setOption(null);
        resetForm();
    };

    const handleCancel = () => {
        if (hasChanges()) {
            setShowMessage(true);
            return;
        }

        closeForm();
    };

    const loadTags = async () => {
        setLoadingTags(true);
        const { tags: fetchedTags } = await employeeService.getTags({
            params: {},
            alert,
        });

        const hasAssignedTags = (authorization?.tagIds.length ?? 0) > 0;
        const visibleTags = !hasAssignedTags ? fetchedTags : fetchedTags.filter((tag) => hasTagAccess(tag.value));

        setTagCatalog(fetchedTags);
        setTags(visibleTags);
        setLoadingTags(false);
    };

    const loadExistingRanges = async () => {
        if (rowsSelected.length !== 1) {
            setExistingRanges([]);
            return;
        }

        setLoadingRanges(true);
        const { ranges } = await employeeService.getTagsByEmployee({
            employeeId: rowsSelected[0].id,
            alert,
        });
        const uniqueRanges = deduplicateRanges(ranges);
        setExistingRanges(ranges);
        setDraftExistingRanges(uniqueRanges);
        setDraftRangeChanges({});
        setDeletedRangeKeys(new Set());
        setLoadingRanges(false);
    };

    const validate = () => {
        const nextErrors: UseFormAddTagValidationErrors = {
            tags: {
                error: selectedTags.length === 0,
                message: selectedTags.length === 0 ? t('assign_tag.validation.tags') : '',
            },
            startDate: {
                error: !startDate?.isValid(),
                message: !startDate?.isValid() ? t('assign_tag.validation.start_date') : '',
            },
            endDate: {
                error: !endDate?.isValid() || hasInvalidDateRange,
                message: hasInvalidDateRange ? t('assign_tag.validation.date_range') : !endDate?.isValid() ? t('assign_tag.validation.end_date') : '',
            },
        };

        setValidationErrors(nextErrors);
        return Object.values(nextErrors).some((field) => field.error);
    };

    const getTagName = (tagId: number | string) => tagCatalog.find((tag) => String(tag.value) === String(tagId))?.label || t('assign_tag.existing.tag_fallback', { tagId });

    const getRangeDisplayValues = (range: EmployeeTagRange) => {
        const changes = draftRangeChanges[getRangeKey(range)];
        return {
            tagId: changes?.tagId ?? range.tag_id,
            startDate: changes?.startDate ?? range.start_date,
            endDate: changes?.endDate ?? range.end_date,
        };
    };

    const isRangeEditing = (range: EmployeeTagRange) => editingRangeKey === getRangeKey(range);
    const isRangeDeleted = (range: EmployeeTagRange) => deletedRangeKeys.has(getRangeKey(range));
    const hasRangeChanges = (range: EmployeeTagRange) => Boolean(draftRangeChanges[getRangeKey(range)]);

    const setSafeSelectedTags = (value: string[]) => {
        setSelectedTags(isEditingExisting ? value.slice(-1) : value);
    };

    const saveExistingRanges = async (ranges: EmployeeTagRange[], changes = draftRangeChanges) => {
        if (rowsSelected.length !== 1) return false;

        return employeeService.replaceEmployeeTagRanges({
            params: {
                employeeId: rowsSelected[0].id,
                ranges: toDraftRanges(ranges, changes, deletedRangeKeys),
            },
            alert,
        });
    };

    const editRange = (range: EmployeeTagRange) => {
        const key = getRangeKey(range);
        const changes = draftRangeChanges[key];
        setEditingRangeKey(key);
        setSelectedTags([String(changes?.tagId ?? range.tag_id)]);
        setStartDate(dayjs(changes?.startDate ?? toDateOnly(range.start_date)));
        setEndDate(dayjs(changes?.endDate ?? toDateOnly(range.end_date)));
        setOverwrite(false);
        setValidationErrors(emptyValidationErrors);
        setValidationMessage('');
        setTab('existing');
    };

    const cancelEditRange = () => {
        setEditingRangeKey(null);
        setSelectedTags([]);
        setStartDate(null);
        setEndDate(null);
        setValidationErrors(emptyValidationErrors);
        setValidationMessage('');
    };

    const saveRangeEdit = () => {
        if (!editingRangeKey || validate() || !startDate || !endDate || selectedTags.length !== 1) return;

        const change = {
            tagId: Number(selectedTags[0]),
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD'),
        };
        const originalRange = existingRanges.find((range) => getRangeKey(range) === editingRangeKey);

        setDraftRangeChanges((current) => {
            const next = { ...current };
            if (
                originalRange &&
                change.tagId === originalRange.tag_id &&
                change.startDate === toDateOnly(originalRange.start_date) &&
                change.endDate === toDateOnly(originalRange.end_date)
            ) {
                delete next[editingRangeKey];
            } else {
                next[editingRangeKey] = change;
            }
            return next;
        });
        cancelEditRange();
    };

    const undoRangeEdit = (range: EmployeeTagRange) => {
        const rangeKey = getRangeKey(range);
        setDraftRangeChanges((current) => {
            const next = { ...current };
            delete next[rangeKey];
            return next;
        });

        if (editingRangeKey === rangeKey) cancelEditRange();
    };

    const deleteRange = (range: EmployeeTagRange) => {
        const rangeKey = getRangeKey(range);
        setDeletedRangeKeys((current) => new Set(current).add(rangeKey));

        if (editingRangeKey === rangeKey) {
            cancelEditRange();
        }
    };

    const restoreRange = (range: EmployeeTagRange) => {
        const rangeKey = getRangeKey(range);
        setDeletedRangeKeys((current) => {
            const next = new Set(current);
            next.delete(rangeKey);
            return next;
        });
    };

    const createAndSelectTag = async () => {
        const normalizedName = searchTag.trim();

        if (!normalizedName || !canCreateTag) return;

        const tagExists = tags.some((tag) => tag.label.toLocaleLowerCase() === normalizedName.toLocaleLowerCase());

        if (tagExists) return;

        setLoadingTags(true);
        const createdTag = await employeeService.createTag({
            tag_name: normalizedName,
            alert,
        });

        if (createdTag) {
            setTagCatalog((currentTags) => [...currentTags, createdTag]);
            setTags((currentTags) => [...currentTags, createdTag]);
            setSelectedTags((currentTags) => (isEditingExisting ? [createdTag.value] : [...currentTags, createdTag.value]));
            setSearchTag('');
        }

        setLoadingTags(false);
    };

    const submit = async () => {
        if (rowsSelected.length === 0) {
            setValidationMessage(t('assign_tag.validation.workers'));
            return;
        }

        const shouldValidateEditingFields = isEditingExisting || tab === 'new';
        if (shouldValidateEditingFields && (validate() || !startDate || !endDate)) return;

        setLoading(true);
        let success = false;

        if (isEditingExisting) {
            const pendingChanges = {
                ...draftRangeChanges,
                [editingRangeKey!]: {
                    tagId: Number(selectedTags[0]),
                    startDate: startDate!.format('YYYY-MM-DD'),
                    endDate: endDate!.format('YYYY-MM-DD'),
                },
            };
            success = await saveExistingRanges(draftExistingRanges, pendingChanges);
        } else if (tab === 'existing') {
            success = await saveExistingRanges(draftExistingRanges);
        } else {
            const validStartDate = startDate!;
            const validEndDate = endDate!;

            success = await employeeService.assignTagsToEmployee({
                params: {
                    employeeIds: rowsSelected.map((row) => row.id),
                    tagIds: selectedTags,
                    startDate: validStartDate.format('YYYY-MM-DD'),
                    endDate: validEndDate.format('YYYY-MM-DD'),
                    overwrite,
                },
                alert,
            });
        }

        if (success) {
            await reload();
            closeForm();
        }

        setLoading(false);
    };

    useEffect(() => {
        if (option !== 'assign_tag') return;

        if (rowsSelected.length === 0) {
            setValidationMessage(t('assign_tag.validation.workers'));
            setOption(null);
            return;
        }

        setOpenForm(true);
        setOption(null);
    }, [option, rowsSelected.length, setOption, t]);

    useEffect(() => {
        if (!openForm) return;

        loadTags();
        loadExistingRanges();
    }, [openForm, rowsSelected.length]);

    return {
        t,
        title: t('assign_tag.title'),
        openForm,
        setOpenForm,
        closeForm,
        handleCancel,
        rowsSelected,
        tags,
        selectedTags,
        setSelectedTags: setSafeSelectedTags,
        searchTag,
        setSearchTag,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        overwrite,
        setOverwrite,
        tab,
        setTab,
        existingRanges,
        draftExistingRanges,
        loading,
        loadingTags,
        loadingRanges,
        validationMessage,
        setValidationMessage,
        validationErrors,
        showMessage,
        setShowMessage,
        canAssign,
        canCreateTag,
        canSubmit,
        isEditingExisting,
        hasInvalidDateRange,
        hasChanges,
        getTagName,
        isRangeEditing,
        isRangeDeleted,
        hasRangeChanges,
        getRangeDisplayValues,
        editRange,
        cancelEditRange,
        saveRangeEdit,
        undoRangeEdit,
        deleteRange,
        restoreRange,
        createAndSelectTag,
        submit,
    };
};

export { useFormAddTag };
